import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Run, Student } from "../lib/supabase";
import {
  Calendar,
  MapPin,
  Edit,
  Trash2,
  DollarSign,
  Link as LinkIcon,
  File,
  Eye,
} from "lucide-react";
import LoadingScreen from "../components/ui/LoadingScreen";
import { useAuth } from "../contexts/AuthContext";
import RunStudentsTable from "../components/students/RunStudentsTable";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import SmartBackButton from "../components/ui/SmartBackButton";
import { format, parseISO, isToday, isBefore } from "date-fns";
import { formatDaysOfWeek, getRunDateRange } from "../utils/runs";

interface RunWithSeries extends Run {
  id: string;
  name: string;
  day_of_week: string[];
  start_time: string;
  end_time: string;
  location: string;
  price: number;
  series: {
    code: string;
    id: string;
    title: string;
    type: string;
    total_labs: number;
    labs?: {
      id: string;
      order_number: number;
      title: string;
      lab_resources: {
        id: string;
        type: string;
        title: string;
        url: string;
        file_path: string;
        file_name: string;
      }[];
    }[];
  };
  lab_dates?: LabDate[];
}

interface LabDate {
  id: string;
  run_id: string;
  lab_number: number;
  date: string;
}

const RunDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<RunWithSeries | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tempLabDates, setTempLabDates] = useState<Record<number, string>>({});
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = userRole === "admin";

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    const fetchRunAndStudents = async () => {
      try {
        // Fetch run data with series info
        const { data: runData, error: runError } = await supabase
          .from("runs")
          .select(
            `
            *,
            series:series_id (
              code,
              id,
              title,
              type,
              total_labs,
              labs:labs (
                id,
                order_number,
                title,
                lab_resources (
                  id,
                  type,
                  title,
                  url,
                  file_path,
                  file_name
                )
              )
            )
          `
          )
          .eq("id", id)
          .single();

        if (runError) {
          console.error("Error fetching run:", runError);
          setIsLoading(false);
          return;
        }

        if (!runData) {
          setIsLoading(false);
          return;
        }

        // Fetch lab dates
        const { data: labDatesData, error: labDatesError } = await supabase
          .from("lab_dates")
          .select("*")
          .eq("run_id", id)
          .order("lab_number");

        if (labDatesError) {
          console.error("Error fetching lab dates:", labDatesError);
        }

        // Sort labs by order_number before assigning to run
        if (runData?.series?.labs) {
          runData.series.labs.sort(
            (a: { order_number: number }, b: { order_number: number }) =>
              a.order_number - b.order_number
          );
        }

        // Combine the data
        const runWithDates = {
          ...runData,
          lab_dates: labDatesData || undefined,
        };

        setRun(runWithDates);

        // Fetch students data
        const { data: studentsData, error: studentsError } = await supabase
          .from("students")
          .select(
            `*, 
            run:run_id (
              id, 
              name, 
              day_of_week, 
              start_time, 
              series:series_id (
                id,
                title,
                code
              )
            )`
          )
          .eq("run_id", id);

        if (studentsError) {
          console.error("Error fetching students:", studentsError);
        }

        setStudents(studentsData || []);
      } catch (error) {
        console.error("Error in fetchRunAndStudents:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRunAndStudents();
  }, [id]);

  const handleDeleteRun = async () => {
    if (!run || !isAdmin || !id) return;

    if (
      window.confirm(
        `Are you sure you want to delete this ${ENTITY_NAMES.RUN}? This will also remove all student enrollments.`
      )
    ) {
      try {
        const { error } = await supabase.from("runs").delete().eq("id", id);

        if (error) throw error;

        toast.success(`${ENTITY_NAMES.RUN} deleted successfully`);
        navigate(`/series/${run.series.id}`);
      } catch (error) {
        console.error("Error deleting run:", error);
        toast.error(`Failed to delete ${ENTITY_NAMES.RUN.toLowerCase()}`);
      }
    }
  };

  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (isLoading) return <LoadingScreen />;
  if (!run)
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold mb-2">Run Not Found</h2>
        <SmartBackButton fallbackPath="/series" className="btn btn-primary" />
      </div>
    );

  return (
    <>
      <div className="flex items-center mb-6">
        <SmartBackButton fallbackPath={`/series/${run?.series.id}`} />
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h1 className="">
              {ENTITY_NAMES.RUN} {run.name} of {run.series.code}
            </h1>
            {isAdmin && (
              <div className="mt-4 sm:mt-0 flex space-x-2">
                <Link to={`/runs/${id}/edit`} className="btn btn-edit">
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Link>
                <button onClick={handleDeleteRun} className="btn btn-danger">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2 mb-6">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium">
                  {getRunDateRange(run)}
                  <br />
                  {formatDaysOfWeek(run.day_of_week)}
                  <br /> {formatTime(run.start_time)} -{" "}
                  {formatTime(run.end_time)}
                </span>
              </div>
            </div>
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="text-sm">
                {run.location || "No location specified"}
              </span>
            </div>
            <div className="flex items-center">
              <DollarSign className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="text-sm">{run.price}.00</span>
            </div>
          </div>

          <div className="space-y-6">
            <section className="mt-8">
              <h2 className="mb-4">Students</h2>

              {isLoading ? (
                <LoadingScreen />
              ) : (
                <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                  <RunStudentsTable
                    students={students}
                    isAdmin={isAdmin}
                    showActions={true}
                    run={{
                      id: run.id,
                      price: run.price,
                      series: {
                        total_labs: run.series?.total_labs || 6,
                        labs: run.series?.labs || [],
                      },
                      lab_dates: run.lab_dates || [],
                    }}
                    onDeleteStudent={async (studentId: string) => {
                      if (
                        window.confirm(
                          "Are you sure you want to remove this student from this group?"
                        )
                      ) {
                        try {
                          const { error } = await supabase
                            .from("students")
                            .update({ run_id: null })
                            .eq("id", studentId);

                          if (error) throw error;

                          setStudents(
                            students.filter(
                              (student) => student.id !== studentId
                            )
                          );
                          toast.success(
                            "Student removed from group successfully"
                          );
                        } catch (error) {
                          console.error(
                            "Error removing student from group:",
                            error
                          );
                          toast.error("Failed to remove student from group");
                        }
                      }
                    }}
                  />
                </div>
              )}
            </section>

            <section className="mb-8">
              <h2 className="mb-4">Schedule</h2>
              <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        {run.series.type === "camp" ? "Day Number" : "Lab Number"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Resources
                      </th>
                      {isAdmin && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {run.series.labs
                      ?.sort((a, b) => a.order_number - b.order_number)
                      .map((lab) => {
                        const labDate = run.lab_dates?.find(
                          (d) => d.lab_number === lab.order_number
                        );

                        return (
                          <tr
                            key={lab.id}
                            className={`hover:bg-neutral-50 ${
                              !labDate?.date
                                ? "text-neutral-400 bg-neutral-100"
                                : ""
                            }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span>
                                <Link
                                  to={`/series/${run.series.id}/labs/${lab.id}`}
                                  className="font-medium text-link hover:text-hover"
                                >
                                  {run.series.type === "camp" ? "Day" : "Lab"} {lab.order_number}
                                </Link>
                                {(labDate?.date &&
                                  isToday(parseISO(labDate.date))) ||
                                (labDate?.date &&
                                  !isBefore(
                                    parseISO(labDate.date),
                                    new Date()
                                  ) &&
                                  !run.lab_dates?.some(
                                    (d) =>
                                      d.date &&
                                      !isBefore(parseISO(d.date), new Date()) &&
                                      parseISO(d.date) < parseISO(labDate.date)
                                  )) ? (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-primary-800">
                                    Next
                                  </span>
                                ) : null}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm whitespace-nowrap">
                              <span>{lab.title}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isAdmin ? (
                                <input
                                  type="date"
                                  className="form-input text-sm border-neutral-300 rounded-md focus:border-secondary-500 focus:ring-secondary-500"
                                  value={
                                    tempLabDates[lab.order_number] !== undefined
                                      ? tempLabDates[lab.order_number]
                                      : labDate?.date || ""
                                  }
                                  onChange={(e) => {
                                    setTempLabDates((prev) => ({
                                      ...prev,
                                      [lab.order_number]: e.target.value,
                                    }));
                                  }}
                                  onBlur={async (e) => {
                                    const newDate = e.target.value;
                                    const currentDate = labDate?.date || "";

                                    // Only save if the date has changed
                                    if (newDate === currentDate) return;

                                    try {
                                      // Set temp state immediately to ensure UI persistence
                                      if (!newDate && labDate) {
                                        // For deletions, set empty string immediately
                                        setTempLabDates((prev) => ({
                                          ...prev,
                                          [lab.order_number]: "",
                                        }));
                                      } else {
                                        // For updates/inserts, clear temp state immediately
                                        setTempLabDates((prev) => {
                                          const newState = { ...prev };
                                          delete newState[lab.order_number];
                                          return newState;
                                        });
                                      }

                                      if (!newDate && labDate) {
                                        // Delete existing date when cleared
                                        const { error } = await supabase
                                          .from("lab_dates")
                                          .delete()
                                          .eq("id", labDate.id);

                                        if (error) {
                                          throw error;
                                        }
                                      } else if (newDate && labDate) {
                                        // Update existing date
                                        const { error } = await supabase
                                          .from("lab_dates")
                                          .update({ date: newDate })
                                          .eq("id", labDate.id);
                                        if (error) throw error;
                                      } else if (newDate && !labDate) {
                                        // Insert new date
                                        const { error } = await supabase
                                          .from("lab_dates")
                                          .insert({
                                            run_id: id,
                                            lab_number: lab.order_number,
                                            date: newDate,
                                          });
                                        if (error) throw error;
                                      }

                                      // Refresh the run data to get updated dates
                                      const {
                                        data: labDatesData,
                                        error: refreshError,
                                      } = await supabase
                                        .from("lab_dates")
                                        .select("*")
                                        .eq("run_id", id)
                                        .order("lab_number");

                                      if (refreshError) {
                                        throw refreshError;
                                      }

                                      setRun((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              lab_dates:
                                                labDatesData || undefined,
                                            }
                                          : null
                                      );

                                      toast.success("Saved.");
                                    } catch (error) {
                                      console.error(
                                        "Error managing lab date:",
                                        error
                                      );
                                      toast.error("Something went wrong.");

                                      // Reset temp state on error
                                      setTempLabDates((prev) => {
                                        const newState = { ...prev };
                                        delete newState[lab.order_number];
                                        return newState;
                                      });
                                    }
                                  }}
                                />
                              ) : (
                                <span>
                                  {labDate?.date
                                    ? format(
                                        parseISO(labDate.date),
                                        "MMM d, yyyy"
                                      )
                                    : "Not scheduled"}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {lab.lab_resources &&
                              lab.lab_resources.length > 0 ? (
                                <div className="space-y-1">
                                  {lab.lab_resources.map((resource) => (
                                    <div
                                      key={resource.id}
                                      className="flex items-center"
                                    >
                                      {resource.type === "url" ? (
                                        <LinkIcon className="h-4 w-4 mr-2 flex-shrink-0 text-neutral-700" />
                                      ) : (
                                        <File className="h-4 w-4 mr-2 flex-shrink-0 text-neutral-700" />
                                      )}
                                      <a
                                        href={
                                          resource.type === "url"
                                            ? resource.url
                                            : `${
                                                import.meta.env
                                                  .VITE_SUPABASE_URL
                                              }/storage/v1/object/public/lab-resources/${
                                                resource.file_path
                                              }?download=${encodeURIComponent(
                                                resource.file_name || ""
                                              )}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-link hover:text-hover hover:opacity-75 transition-all duration-200 truncate"
                                      >
                                        {resource.title ||
                                          (resource.type === "url"
                                            ? resource.url
                                            : resource.file_name)}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-neutral-500">-</span>
                              )}
                            </td>
                            {isAdmin && (
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className="flex justify-end space-x-2">
                                  <Link
                                    to={`/series/${run.series.id}/labs/${lab.id}`}
                                    className="icon-view"
                                    title="View lab details"
                                  >
                                    <Eye className="h-5 w-5" />
                                  </Link>
                                  <Link
                                    to={`/series/${run.series.id}/labs/${lab.id}/edit`}
                                    className="icon-edit"
                                    title="Edit lab"
                                  >
                                    <Edit className="h-5 w-5" />
                                  </Link>
                                  {labDate && (
                                    <button
                                      onClick={async () => {
                                        if (
                                          window.confirm(
                                            "Are you sure you want to remove this date?"
                                          )
                                        ) {
                                          try {
                                            const { error } = await supabase
                                              .from("lab_dates")
                                              .delete()
                                              .eq("id", labDate.id);
                                            if (error) throw error;

                                            // Remove the date from state
                                            setRun((prev) =>
                                              prev
                                                ? {
                                                    ...prev,
                                                    lab_dates:
                                                      prev.lab_dates?.filter(
                                                        (d) =>
                                                          d.id !== labDate.id
                                                      ),
                                                  }
                                                : null
                                            );

                                            toast.success("Saved.");
                                          } catch (error) {
                                            console.error(
                                              "Error removing lab date:",
                                              error
                                            );
                                            toast.error("Something went wrong.");
                                          }
                                        }
                                      }}
                                      className="icon-delete"
                                      title="Remove lab date"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default RunDetail;
